import React, { useState, useEffect } from 'react';
import { Slider, Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styles from './SubmissionTimeline.scss';

const formatTimestamp = timestamp => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const SubmissionTimeline = ({
  curr_submission,
  allSubmissions,
  onSubmissionChange,
  testStartTime,
  testEndTime,
}) => {
  const { t } = useTranslation('practice_tests');
  const [selectedSubmission, setSelectedSubmission] = useState(curr_submission);

  useEffect(() => {
    setSelectedSubmission(curr_submission);
  }, [curr_submission]);

  let submissionsArray = [];

  if (Array.isArray(allSubmissions)) {
    submissionsArray = allSubmissions;
  } else if (allSubmissions && typeof allSubmissions === 'object') {
    submissionsArray = Object.values(allSubmissions);
  }

  submissionsArray = submissionsArray.sort(
    (a, b) =>
      new Date(a.creation_date || a.submitted_at) - new Date(b.creation_date || b.submitted_at),
  );

  if (!submissionsArray || submissionsArray.length === 0) {
    const marks = {
      50: {
        style: { color: '#1890FF' },
        label: (
          <div className={styles['submission-timeline__mark-label']}>
            <div className={styles['submission-timeline__mark-points']}>
              {t('practice_tests.test_result.points_text', {
                points: curr_submission?.score || curr_submission?.points || 0,
              })}
            </div>
            <div className={styles['submission-timeline__mark-time--selected']}>
              {formatTimestamp(curr_submission?.creation_date || curr_submission?.submitted_at)}
            </div>
          </div>
        ),
      },
    };

    return (
      <div
        className={styles['submission-timeline']}
        data-test-id="submission-timeline"
        data-specific-id="submissions-timeline-container"
      >
        <div
          className={styles['submission-timeline__title']}
          data-test-id="timeline-subtitle"
          data-specific-id="choose-time-label"
        >
          {t('practice_tests.test_result.choose_submission_time')}
        </div>
        <div
          className={styles['submission-timeline__controls']}
          data-test-id="timeline-controls"
          data-specific-id="slider-controls-container"
        >
          <Button
            type="text"
            icon={<LeftOutlined />}
            disabled={true}
            className={styles['submission-timeline__nav-button--disabled']}
            data-test-id="previous-submission-button"
            data-specific-id="navigate-previous-submission"
          />
          <div className={styles['submission-timeline__slider-wrapper']}>
            <Slider
              className={styles['submission-timeline__slider']}
              value={50}
              marks={marks}
              disabled={true}
              tooltip={{ open: false }}
              data-test-id="submission-slider"
              data-specific-id="submission-timeline-slider"
              data-action="select-submission"
            />
          </div>
          <Button
            type="text"
            icon={<RightOutlined />}
            disabled={true}
            className={styles['submission-timeline__nav-button--disabled']}
            data-test-id="next-submission-button"
            data-specific-id="navigate-next-submission"
          />
        </div>
      </div>
    );
  }

  const getTimeBasedPosition = submission => {
    const PADDING_PERCENT = 10;
    const MIN_POSITION = PADDING_PERCENT;
    const MAX_POSITION = 100 - PADDING_PERCENT;

    if (submissionsArray.length === 0) return 50;
    if (submissionsArray.length === 1) return 50;

    const submissionTime = new Date(submission.creation_date || submission.submitted_at).getTime();
    const firstSubmissionTime = new Date(
      submissionsArray[0].creation_date || submissionsArray[0].submitted_at,
    ).getTime();
    const lastSubmissionTime = new Date(
      submissionsArray[submissionsArray.length - 1].creation_date ||
        submissionsArray[submissionsArray.length - 1].submitted_at,
    ).getTime();

    const startTime = testStartTime ? new Date(testStartTime).getTime() : firstSubmissionTime;
    const endTime = testEndTime ? new Date(testEndTime).getTime() : lastSubmissionTime;

    const totalDuration = endTime - startTime;

    if (totalDuration === 0) return 50;

    const timeSinceStart = submissionTime - startTime;
    const rawPosition = (timeSinceStart / totalDuration) * 100;

    const position = MIN_POSITION + (rawPosition * (MAX_POSITION - MIN_POSITION)) / 100;

    return Math.max(MIN_POSITION, Math.min(MAX_POSITION, position));
  };

  const marks = {};
  const positionToSubmissionMap = {};

  submissionsArray.forEach((submission, index) => {
    const position = getTimeBasedPosition(submission);
    const isSelected = submission.id === selectedSubmission?.id;

    marks[position] = {
      style: {
        color: isSelected ? '#1890FF' : '#9CA3AF',
      },
      label: (
        <div className={styles['submission-timeline__mark-label']}>
          <div className={styles['submission-timeline__mark-points']}>
            {t('practice_tests.test_result.points_text', {
              points: submission.score || submission.points || 0,
            })}
          </div>
          <div
            className={
              styles[`submission-timeline__mark-time--${isSelected ? 'selected' : 'unselected'}`]
            }
          >
            {formatTimestamp(submission.creation_date || submission.submitted_at)}
          </div>
        </div>
      ),
    };

    positionToSubmissionMap[position] = submission;
  });

  const currentIndex = submissionsArray.findIndex(s => s.id === selectedSubmission?.id);
  const currentPosition =
    currentIndex >= 0 ? getTimeBasedPosition(submissionsArray[currentIndex]) : 0;

  const handleSliderChange = value => {
    // Find the closest submission to the slider value
    const positions = Object.keys(marks)
      .map(Number)
      .sort((a, b) => a - b);
    const closestPosition = positions.reduce((prev, curr) =>
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev,
    );

    const newSubmission = positionToSubmissionMap[closestPosition];

    if (newSubmission && newSubmission.id !== selectedSubmission?.id) {
      setSelectedSubmission(newSubmission);
      if (onSubmissionChange) {
        onSubmissionChange(newSubmission);
      }
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newSubmission = submissionsArray[currentIndex - 1];
      setSelectedSubmission(newSubmission);
      if (onSubmissionChange) {
        onSubmissionChange(newSubmission);
      }
    }
  };

  const handleNext = () => {
    if (currentIndex < submissionsArray.length - 1) {
      const newSubmission = submissionsArray[currentIndex + 1];
      setSelectedSubmission(newSubmission);
      if (onSubmissionChange) {
        onSubmissionChange(newSubmission);
      }
    }
  };

  const isDisabledPrev = currentIndex <= 0 || submissionsArray.length <= 1;
  const isDisabledNext =
    currentIndex >= submissionsArray.length - 1 || submissionsArray.length <= 1;

  return (
    <div
      className={styles['submission-timeline']}
      data-test-id="submission-timeline"
      data-specific-id="submissions-timeline-container"
    >
      <div
        className={styles['submission-timeline__title']}
        data-test-id="timeline-subtitle"
        data-specific-id="choose-time-label"
      >
        {t('practice_tests.test_result.choose_submission_time')}
      </div>
      <div
        className={styles['submission-timeline__controls']}
        data-test-id="timeline-controls"
        data-specific-id="slider-controls-container"
      >
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={handlePrevious}
          disabled={isDisabledPrev}
          className={
            styles[`submission-timeline__nav-button--${isDisabledPrev ? 'disabled' : 'enabled'}`]
          }
          data-test-id="previous-submission-button"
          data-specific-id="navigate-previous-submission"
        />
        <div className={styles['submission-timeline__slider-wrapper']}>
          <Slider
            className={styles['submission-timeline__slider']}
            value={currentPosition}
            onChange={handleSliderChange}
            marks={marks}
            step={null}
            disabled={submissionsArray.length <= 1}
            tooltip={{ open: false }}
            data-test-id="submission-slider"
            data-specific-id="submission-timeline-slider"
            data-action="select-submission"
          />
        </div>
        <Button
          type="text"
          icon={<RightOutlined />}
          onClick={handleNext}
          disabled={isDisabledNext}
          className={
            styles[`submission-timeline__nav-button--${isDisabledNext ? 'disabled' : 'enabled'}`]
          }
          data-test-id="next-submission-button"
          data-specific-id="navigate-next-submission"
        />
      </div>
    </div>
  );
};

export default SubmissionTimeline;
